// The filters of the Photos page live in its URL (docs/PHOTOS.md §11), so they can be bookmarked and Back works:
//   /photos?tag=7            only photos with tag 7
//   /photos?favorites=1      only the current user's favorites
//   /photos?trash=1          the Trash view
//   /photos?album=3          only photos in album 3 (the album page)
//   /photos?q=beach&kind=video&since=2024-01-01&until=2024-12-31   search words, photos or videos, taken between two dates
// The lightbox forwards the same filters when it asks for neighbours, so ← → stay inside the filtered set.

import { appUrl } from './page';

export interface PhotoFilter {
  tag: number | null;
  favorites: boolean;
  trash: boolean;
  album: number | null;
  /** Words to search for in titles, descriptions, file names, camera and tag names. */
  q: string;
  kind: 'Photo' | 'Video' | null;
  /** Taken on or after / on or before (yyyy-mm-dd). */
  since: string | null;
  until: string | null;
}

export const noFilter: PhotoFilter = { tag: null, favorites: false, trash: false, album: null, q: '', kind: null, since: null, until: null };

export function readFilter(search: string): PhotoFilter {
  const params = new URLSearchParams(search);
  const tag = params.get('tag');
  const album = params.get('album');
  const kind = params.get('kind')?.toLowerCase();
  const date = (name: string) => {
    const value = params.get(name);
    return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  };
  return {
    tag: tag && /^\d+$/.test(tag) ? Number(tag) : null,
    favorites: params.get('favorites') === '1',
    trash: params.get('trash') === '1',
    album: album && /^\d+$/.test(album) ? Number(album) : null,
    q: (params.get('q') ?? '').trim(),
    kind: kind === 'photo' ? 'Photo' : kind === 'video' ? 'Video' : null,
    since: date('since'),
    until: date('until'),
  };
}

export function isFiltered(filter: PhotoFilter): boolean {
  return filter.tag !== null || filter.favorites || filter.trash || filter.album !== null || hasSearch(filter);
}

/** Any of the search controls (words, type, dates) in use. */
export function hasSearch(filter: PhotoFilter): boolean {
  return filter.q !== '' || filter.kind !== null || filter.since !== null || filter.until !== null;
}

/** Query parameters for the typed API client (undefined values are left out). */
export function apiQuery(filter: PhotoFilter): { tag?: number; favorites?: boolean; trash?: boolean; album?: number; q?: string; kind?: 'Photo' | 'Video'; since?: string; until?: string } {
  return { tag: filter.tag ?? undefined, favorites: filter.favorites || undefined, trash: filter.trash || undefined, album: filter.album ?? undefined,
    q: filter.q || undefined, kind: filter.kind ?? undefined, since: filter.since ?? undefined, until: filter.until ?? undefined,
  };
}

/** App-relative link to the library with this filter, e.g. "/HomeLab/photos?tag=7&favorites=1". */
export function filterHref(filter: Partial<PhotoFilter>): string {
  const params = new URLSearchParams();
  if (filter.tag != null) params.set('tag', String(filter.tag));
  if (filter.favorites) params.set('favorites', '1');
  if (filter.trash) params.set('trash', '1');
  if (filter.album != null) params.set('album', String(filter.album));
  if (filter.q) params.set('q', filter.q);
  if (filter.kind) params.set('kind', filter.kind.toLowerCase());
  if (filter.since) params.set('since', filter.since);
  if (filter.until) params.set('until', filter.until);
  const query = params.toString();
  return appUrl('photos') + (query ? `?${query}` : '');
}
